import React, { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronRightIcon, WrenchScrewdriverIcon, LightBulbIcon, DocumentIcon } from '@heroicons/react/24/outline';
import type { ChatSource } from '@archie/api-shared';
import SourceChips from '../sourceChips/SourceChips';

export interface ChatBubbleAttachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  kind: 'image' | 'document';
  width?: number | null;
  height?: number | null;
}

export type AssistantStep =
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool_call'; toolName: string; input: unknown }
  | { kind: 'tool_result'; toolName: string; output: string };

interface ChatBubbleProps {
  messageRole: 'user' | 'assistant';
  content: string;
  steps?: AssistantStep[];
  sources?: ChatSource[];
  attachments?: ChatBubbleAttachment[];
  apiUrl?: string;
  highlightedSourceKey?: string | null;
  onSelectSource?: (key: string) => void;
  defaultStepsExpanded?: boolean;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentsList: React.FC<{ attachments: ChatBubbleAttachment[]; apiUrl: string }> = ({
  attachments,
  apiUrl
}) => {
  return (
    <div className="flex flex-wrap gap-2 mb-2">
      {attachments.map((a) => {
        const url = `${apiUrl}/chat-attachments/${a.id}/content`;
        if (a.kind === 'image') {
          return (
            <a
              key={a.id}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="block max-w-[240px] max-h-[240px] overflow-hidden rounded-lg border border-(--color-border-default)"
            >
              <img src={url} alt={a.filename} loading="lazy" className="block max-h-[240px] w-auto" />
            </a>
          );
        }
        return (
          <a
            key={a.id}
            href={`${url}?download=1`}
            className="flex items-center gap-2 border border-(--color-border-default) bg-(--color-surface-subtle) rounded-lg px-2 py-1 max-w-[260px] text-sm text-(--color-text-primary) hover:bg-(--color-surface-hover)"
            title={a.filename}
          >
            <DocumentIcon className="h-5 w-5 shrink-0 text-(--color-text-muted)" />
            <div className="min-w-0">
              <div className="truncate text-xs">{a.filename}</div>
              <div className="text-[11px] text-(--color-text-muted)">{formatBytes(a.sizeBytes)}</div>
            </div>
          </a>
        );
      })}
    </div>
  );
};

const renderInput = (input: unknown): string => {
  if (input == null) return '';
  if (typeof input === 'string') return input;
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
};

const StepsDisclosure: React.FC<{ steps: AssistantStep[]; defaultOpen: boolean }> = ({ steps, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  // Sync open state when the parent's preference changes so toggling the
  // "Show reasoning by default" lightbulb applies to bubbles that are
  // already on screen, not just newly-rendered ones.
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);
  const counts = steps.reduce(
    (acc, s) => {
      if (s.kind === 'reasoning') acc.reasoning++;
      else if (s.kind === 'tool_call') acc.tools++;
      return acc;
    },
    { reasoning: 0, tools: 0 }
  );
  const summaryParts: string[] = [];
  if (counts.tools > 0) summaryParts.push(`${counts.tools} tool ${counts.tools === 1 ? 'call' : 'calls'}`);
  if (counts.reasoning > 0)
    summaryParts.push(`${counts.reasoning} reasoning ${counts.reasoning === 1 ? 'step' : 'steps'}`);
  const summary = summaryParts.join(' · ') || `${steps.length} steps`;

  return (
    <div className="mt-2 text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-(--color-text-muted) hover:text-(--color-text-secondary)"
        aria-expanded={open}
      >
        <ChevronRightIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span>{open ? 'Hide steps' : `Show steps (${summary})`}</span>
      </button>
      {open && (
        <ol className="mt-2 space-y-2 border-l-2 border-(--color-border-default) pl-3">
          {steps.map((step, i) => {
            if (step.kind === 'reasoning') {
              return (
                <li key={i} className="text-(--color-text-muted)">
                  <div className="flex items-center gap-1 text-xs uppercase tracking-wide">
                    <LightBulbIcon className="h-3 w-3" />
                    <span>Reasoning</span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm">{step.text}</div>
                </li>
              );
            }
            if (step.kind === 'tool_call') {
              return (
                <li key={i} className="text-(--color-text-muted)">
                  <div className="flex items-center gap-1 text-xs uppercase tracking-wide">
                    <WrenchScrewdriverIcon className="h-3 w-3" />
                    <span>Tool · {step.toolName}</span>
                  </div>
                  {step.input != null && (
                    <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words bg-(--color-surface-subtle) rounded p-1 mt-1">
                      {renderInput(step.input)}
                    </pre>
                  )}
                </li>
              );
            }
            // tool_result
            return (
              <li key={i} className="text-(--color-text-muted)">
                <div className="text-xs uppercase tracking-wide">Result · {step.toolName}</div>
                <div className="text-xs whitespace-pre-wrap break-words bg-(--color-surface-subtle) rounded p-1 mt-1 max-h-48 overflow-y-auto">
                  {step.output}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const SourcesDisclosure: React.FC<{
  sources: ChatSource[];
  highlightedKey?: string | null;
  onSelect?: (key: string) => void;
}> = ({ sources, highlightedKey, onSelect }) => {
  const [open, setOpen] = useState(false);

  // Auto-open if a source is highlighted (e.g., user clicked a citation in a
  // panel) so the chip they're trying to find isn't hidden.
  useEffect(() => {
    if (highlightedKey) setOpen(true);
  }, [highlightedKey]);

  return (
    <div className="mt-2 text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-(--color-text-muted) hover:text-(--color-text-secondary)"
        aria-expanded={open}
      >
        <ChevronRightIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span>{open ? 'Hide sources' : `Show sources (${sources.length})`}</span>
      </button>
      {open && <SourceChips sources={sources} highlightedKey={highlightedKey} onSelect={onSelect} />}
    </div>
  );
};

const ChatBubble: React.FC<ChatBubbleProps> = ({
  messageRole,
  content,
  steps,
  sources,
  attachments,
  apiUrl,
  highlightedSourceKey,
  onSelectSource,
  defaultStepsExpanded = false
}) => {
  const isUser = messageRole === 'user';
  const hasSteps = !isUser && Array.isArray(steps) && steps.length > 0;
  const hasSources = !isUser && Array.isArray(sources) && sources.length > 0;
  const hasAttachments = isUser && Array.isArray(attachments) && attachments.length > 0;

  return (
    <div className={`flex px-4 w-full mx-auto max-w-[800px] ${isUser ? 'justify-end mt-2' : 'justify-start mt-4'}`}>
      <div
        className={`chat-bubble-content inline-block px-4 py-2 whitespace-pre-wrap rounded-2xl ${
          isUser
            ? 'bg-(--color-accent) text-white rounded-br-sm max-w-[85%]'
            : 'bg-(--color-chat-bubble) text-(--color-text-primary) rounded-bl-sm max-w-[90%]'
        }`}
      >
        {hasAttachments && apiUrl && <AttachmentsList attachments={attachments!} apiUrl={apiUrl} />}
        {content && (
          <div className="markdown-content">
            <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
          </div>
        )}
        {hasSources && (
          <SourcesDisclosure sources={sources!} highlightedKey={highlightedSourceKey} onSelect={onSelectSource} />
        )}
        {hasSteps && <StepsDisclosure steps={steps!} defaultOpen={defaultStepsExpanded} />}
      </div>
    </div>
  );
};

export default ChatBubble;
